import { useRef, useState } from 'react';
import { api } from '../api';
import type { BillingMethod, Project, ProjectStatus, User, Workspace } from '../api';
import { useSession } from '../session';
import { useData } from '../useData';
import { useStatusName } from './bits';
import { RichText } from './RichText';
import type { RichTextHandle } from './RichText';
import { TagSelect } from './TagSelect';

const STATUSES: ProjectStatus[] = [
  'planning', 'in_progress', 'on_hold', 'complete', 'archived', 'placeholder',
];

const METHODS: BillingMethod[] = ['prepaid', 'recurring', 'occasional'];

/** Creating a project and editing one are the same eight fields, so they are
 *  the same form. Editing adds a ninth, the About text.
 *
 *  Who is on the project is one of them. Membership is what lets a client see
 *  a project at all, and while it lived only in a corner of the About tab a
 *  new project went out with nobody on it and the client could not find it.
 *  A new project starts with the people already on that client's projects,
 *  shown as tags before anything is saved, so who will see it is decided here
 *  rather than discovered later.
 *
 *  There is no delete. A project carries the hours a client was billed for, and
 *  Drupal would not delete one either; taking it out of their view is what
 *  "visible to the client" is for. */
export function ProjectForm({
  project, workspaceId, onDone, onCancel,
}: {
  project?: Project;
  workspaceId?: number;
  onDone: (saved: Project) => void;
  onCancel: () => void;
}) {
  const { t } = useSession();
  const statusName = useStatusName();

  const workspaces = useData(() => api.workspaces(), []);
  const [form, setForm] = useState({
    workspaceId: project?.workspaceId ?? workspaceId ?? 0,
    title: project?.title ?? '',
    status: (project?.status ?? 'planning') as ProjectStatus,
    totalHours: project?.totalHours != null ? String(project.totalHours) : '',
    startDate: project?.startDate?.slice(0, 10) ?? '',
    endDate: project?.endDate?.slice(0, 10) ?? '',
    managerId: project?.managerId ?? 0,
    isPublished: project?.isPublished ?? true,
    billingMethod: (project?.billingMethod ?? 'occasional') as BillingMethod,
    billingPeriodMonths: project?.billingPeriodMonths ?? 1,
    // Euros in the form, cents in the database, converted in one place.
    rate: project?.hourlyRateCents != null ? (project.hourlyRateCents / 100).toFixed(2) : '',
  });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const about = useRef<RichTextHandle>(null);
  const [uploading, setUploading] = useState(false);
  // A new project is saved before its people are added. If adding them fails,
  // the next try has to finish that project, not make another one.
  const [created, setCreated] = useState<Project | null>(null);

  // Only people who can see the client can be given its projects.
  const people = useData<User[]>(
    () => (form.workspaceId ? api.workspacePeople(form.workspaceId) : Promise.resolve([])),
    [form.workspaceId],
  );

  // Anyone can be put on a project — that is how a client's first person gets
  // there — so the choice is everybody, not only the client's people.
  const everyone = useData(() => api.people(), []);
  const current = useData<User[]>(
    () => (project ? api.projectPeople(project.id) : Promise.resolve([])),
    [project?.id],
  );
  // null until somebody touches the field: until then it follows the project's
  // people, or, for a new one, the client's.
  const [members, setMembers] = useState<number[] | null>(null);
  const before = (current.data ?? []).map((u) => u.id);
  const chosen = members ?? (project ? before : (people.data ?? []).map((u) => u.id));
  const loadingPeople = !everyone.data || (!!project && !current.data);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);

    // An empty box is an instruction, not a silence: it travels as null so
    // the field is cleared. undefined would be dropped by JSON.stringify and
    // read as "leave it alone", which is how a rate somebody deleted kept
    // coming back.
    const payload = {
      title: form.title,
      status: form.status,
      totalHours: form.totalHours ? Number(form.totalHours) : null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      managerId: form.managerId || null,
      isPublished: form.isPublished,
      billingMethod: form.billingMethod,
      billingPeriodMonths: form.billingPeriodMonths,
      hourlyRateCents: form.rate.trim()
        ? Math.round(parseFloat(form.rate.replace(',', '.')) * 100)
        : null,
      // Only an existing project has the editor: an image pasted into it is
      // granted against the project, and a new one has no id to grant it to.
      ...(project ? { descriptionHtml: about.current?.html() ?? '' } : {}),
    };

    try {
      const target = project ?? created;
      const saved = target
        ? await api.updateProject(target.id, payload)
        : await api.createProject({ ...payload, workspaceId: form.workspaceId });
      if (!target) setCreated(saved);
      // After a failed try some of the people may be on it already; ask.
      const had = created ? (await api.projectPeople(saved.id)).map((u) => u.id) : before;
      await Promise.all([
        ...chosen.filter((id) => !had.includes(id)).map((id) => api.addProjectMember(saved.id, id)),
        ...had.filter((id) => !chosen.includes(id)).map((id) => api.removeProjectMember(saved.id, id)),
      ]);
      onDone(saved);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card stack" style={{ marginBottom: 'var(--sp-5)' }}>
      <div className="row">
        {!project && (
          <div style={{ flex: 1, minWidth: 200 }}>
            <label htmlFor="p-client">{t.client}</label>
            <TagSelect
              id="p-client"
              value={form.workspaceId || undefined}
              placeholder={t.search}
              disabled={!!created}
              options={(workspaces.data ?? []).map((w: Workspace) => ({ value: w.id, label: w.name }))}
              onChange={(v) => {
                setForm({ ...form, workspaceId: v ?? 0, managerId: 0 });
                setMembers(null);
              }}
            />
          </div>
        )}
        <div style={{ flex: 2, minWidth: 240 }}>
          <label htmlFor="p-title">{t.title}</label>
          <input id="p-title" required value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
      </div>

      <div className="row">
        <div style={{ minWidth: 170 }}>
          <label htmlFor="p-status">{t.status}</label>
          <select id="p-status" value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value as ProjectStatus })}>
            {STATUSES.map((st) => <option key={st} value={st}>{statusName(st)}</option>)}
          </select>
        </div>
        <div style={{ maxWidth: 130 }}>
          <label htmlFor="p-hours">{t.totalHours}</label>
          <input id="p-hours" inputMode="numeric" value={form.totalHours}
            onChange={(e) => setForm({ ...form, totalHours: e.target.value })} />
        </div>
        <div style={{ maxWidth: 160 }}>
          <label htmlFor="p-start">{t.startDate}</label>
          <input id="p-start" type="date" value={form.startDate}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
        </div>
        <div style={{ maxWidth: 160 }}>
          <label htmlFor="p-end">{t.endDate}</label>
          <input id="p-end" type="date" value={form.endDate}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
        </div>
        <div style={{ minWidth: 180 }}>
          <label htmlFor="p-manager">{t.manager}</label>
          <TagSelect
            id="p-manager"
            value={form.managerId || undefined}
            placeholder={t.none}
            options={(people.data ?? []).map((u) => ({ value: u.id, label: u.name }))}
            onChange={(v) => setForm({ ...form, managerId: v ?? 0 })}
          />
        </div>
      </div>

      <div>
        <label htmlFor="p-people">{t.people}</label>
        <TagSelect
          id="p-people"
          multiple
          value={chosen}
          placeholder={loadingPeople ? `${t.loading}…` : t.noPeople}
          disabled={loadingPeople}
          options={(everyone.data ?? [])
            .filter((u) => u.isActive || chosen.includes(u.id))
            .map((u) => ({ value: u.id, label: u.name, hint: u.email }))}
          onChange={setMembers}
        />
      </div>

      <div className="row">
        <div style={{ minWidth: 180 }}>
          <label htmlFor="p-method">{t.method}</label>
          <select
            id="p-method"
            value={form.billingMethod}
            onChange={(e) => setForm({ ...form, billingMethod: e.target.value as BillingMethod })}
          >
            {METHODS.map((m) => <option key={m} value={m}>{t[m]}</option>)}
          </select>
        </div>
        {form.billingMethod === 'recurring' && (
          <div style={{ maxWidth: 120 }}>
            <label htmlFor="p-months">{t.period}</label>
            <select
              id="p-months"
              value={form.billingPeriodMonths}
              onChange={(e) => setForm({ ...form, billingPeriodMonths: Number(e.target.value) })}
            >
              {[1, 2, 3, 6, 12].map((n) => (
                <option key={n} value={n}>{t.everyMonths(n)}</option>
              ))}
            </select>
          </div>
        )}
        <div style={{ maxWidth: 130 }}>
          <label htmlFor="p-rate">{t.rate}</label>
          <input
            id="p-rate"
            inputMode="decimal"
            placeholder="80.00"
            value={form.rate}
            onChange={(e) => setForm({ ...form, rate: e.target.value })}
          />
        </div>
      </div>

      {project && (
        <div>
          <label id="p-about-label">{t.about}</label>
          <RichText
            ref={about}
            labelledBy="p-about-label"
            initialHtml={project.descriptionHtml}
            owner={{ type: 'project', id: project.id }}
            onPendingChange={setUploading}
          />
        </div>
      )}

      <label style={{ textTransform: 'none', letterSpacing: 0 }}>
        <input type="checkbox" checked={form.isPublished}
          onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
          style={{ width: 'auto', marginRight: 8 }} />
        {t.published}
      </label>

      {problem && <p className="error" role="alert">{problem}</p>}
      <div className="row">
        <button className="primary" type="submit" disabled={busy || uploading || loadingPeople || people.loading || !form.title || !form.workspaceId}>
          {project ? t.save : t.create} &gt;
        </button>
        <button type="button" onClick={onCancel}>{t.cancel}</button>
      </div>
    </form>
  );
}
